import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'

// ── GoodGuardian Logo SVG ─────────────────────────────────────────────────────
const GGLogo = ({ size = 64, variant = 'color' }) => {
  const isLight = variant === 'light'
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="ggShieldGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={isLight ? 'rgba(255,255,255,0.30)' : '#2E75B6'} />
          <stop offset="100%" stopColor={isLight ? 'rgba(255,255,255,0.10)' : '#1F497D'} />
        </linearGradient>
        <linearGradient id="ggShineGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="white" stopOpacity={isLight ? '0.22' : '0.16'} />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Shield body */}
      <path
        d="M40 6 L68 18 L68 40 C68 56 55 68 40 74 C25 68 12 56 12 40 L12 18 Z"
        fill="url(#ggShieldGrad)"
      />
      {/* Shine overlay */}
      <path
        d="M40 6 L68 18 L68 40 C68 56 55 68 40 74 C25 68 12 56 12 40 L12 18 Z"
        fill="url(#ggShineGrad)"
      />
      {/* Inner shield ring */}
      <path
        d="M40 14 L62 23.5 L62 40 C62 53 51 63 40 68 C29 63 18 53 18 40 L18 23.5 Z"
        fill="none" stroke="white" strokeOpacity={isLight ? '0.35' : '0.18'} strokeWidth="1.5"
      />
      {/* Border */}
      {isLight && (
        <path
          d="M40 6 L68 18 L68 40 C68 56 55 68 40 74 C25 68 12 56 12 40 L12 18 Z"
          fill="none" stroke="white" strokeOpacity="0.45" strokeWidth="1.5"
        />
      )}
      {/* Parent silhouette */}
      <circle cx="34" cy="30" r="5.5" fill="white" fillOpacity="0.93" />
      <path d="M25 55 C25 43 43 43 43 55" fill="white" fillOpacity="0.93" />
      {/* Child silhouette */}
      <circle cx="50" cy="33" r="4" fill="white" fillOpacity="0.78" />
      <path d="M44 55 C44 45.5 58 45.5 58 55" fill="white" fillOpacity="0.78" />
      {/* Holding hands */}
      <line x1="43" y1="42" x2="46" y2="43.5" stroke="white" strokeOpacity="0.92" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  )
}

// ── Inline icons ──────────────────────────────────────────────────────────────
const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
  </svg>
)

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
)

// ── Feature list ──────────────────────────────────────────────────────────────
const features = [
  {
    emoji: '🤖',
    title: 'AI Legal Guidance',
    desc: 'Get answers on custody rights, divorce process, and CPS interactions — available 24/7.',
    accent: 'bg-brand-100 border-brand-200',
    iconBg: 'bg-brand-700',
  },
  {
    emoji: '📄',
    title: 'Document Generation',
    desc: 'Auto-draft parenting plans, CPS response letters, custody petitions, and more.',
    accent: 'bg-blue-50 border-blue-200',
    iconBg: 'bg-brand-500',
  },
  {
    emoji: '🛡️',
    title: 'CPS Case Tracker',
    desc: 'Step-by-step guidance through investigations with your rights clearly explained.',
    accent: 'bg-indigo-50 border-indigo-200',
    iconBg: 'bg-indigo-600',
  },
  {
    emoji: '📋',
    title: 'Communication Log',
    desc: 'Keep a court-ready record of all co-parenting communications and custody events.',
    accent: 'bg-sky-50 border-sky-200',
    iconBg: 'bg-sky-600',
  },
  {
    emoji: '💰',
    title: 'Expense Tracker',
    desc: 'Submit, approve, and pay shared child expenses with built-in Apple Pay and Venmo.',
    accent: 'bg-emerald-50 border-emerald-200',
    iconBg: 'bg-emerald-600',
  },
]

const benefits = [
  '50-state jurisdiction coverage',
  'Equal support for moms and dads',
  'Attorney-reviewed document templates',
  'Privacy-first — your data stays yours',
]

const stats = [
  { value: '50', label: 'States covered' },
  { value: '5+', label: 'AI-powered tools' },
  { value: '$0', label: 'To start' },
  { value: '24/7', label: 'Always available' },
]

// ── Component ─────────────────────────────────────────────────────────────────
export default function Landing() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleWaitlist = async (e) => {
    e.preventDefault()
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.')
      return
    }
    setError('')
    setLoading(true)
    await new Promise(r => setTimeout(r, 1000))
    setLoading(false)
    setSubmitted(true)
  }

  return (
    <div className="min-h-full flex flex-col overflow-y-auto no-scrollbar">

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <section
        className="text-white px-6 pb-14 flex flex-col items-center text-center gap-6"
        style={{
          paddingTop: 'calc(3.5rem + env(safe-area-inset-top, 0px))',
          background: 'linear-gradient(160deg, #1a3f70 0%, #1F497D 40%, #2E75B6 100%)',
        }}
      >
        {/* Logo lockup */}
        <div className="flex flex-col items-center gap-3">
          <div className="drop-shadow-xl">
            <GGLogo size={72} variant="light" />
          </div>
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight leading-none">
              GoodGuardian
            </h1>
            <p className="text-xs font-semibold tracking-[0.22em] uppercase text-blue-200/80 mt-1">
              AI Family Law Platform
            </p>
          </div>
        </div>

        {/* Headline */}
        <div className="space-y-2">
          <p className="text-xl font-bold leading-snug max-w-xs">
            Expert family law guidance.<br />
            <span className="text-blue-200">Without the $400/hr bill.</span>
          </p>
          <p className="text-sm text-blue-100/80 leading-relaxed max-w-xs">
            AI-powered support for parents navigating custody, divorce, and CPS proceedings — equally, for every parent.
          </p>
        </div>

        {/* CTA buttons */}
        <div className="flex flex-col gap-3 w-full max-w-xs">
          <button
            onClick={() => navigate('/register')}
            className="w-full py-4 rounded-2xl bg-white text-brand-700 font-extrabold text-base shadow-xl flex items-center justify-center gap-2 active:scale-95 transition-transform"
          >
            Get Started — It's Free
            <ArrowIcon />
          </button>
          <button
            onClick={() => navigate('/login')}
            className="text-sm text-blue-200 hover:text-white font-semibold transition-colors tap-target"
          >
            Already have an account? Sign in →
          </button>
        </div>

        {/* Bottom wave */}
        <div className="absolute left-0 right-0 pointer-events-none" style={{ bottom: -1 }}>
          <svg viewBox="0 0 375 24" preserveAspectRatio="none" className="w-full h-6">
            <path d="M0 24 Q187 0 375 24 L375 24 L0 24 Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* ── Stats strip ────────────────────────────────────────────────────── */}
      <section className="bg-white px-4 pt-6 pb-4">
        <div className="grid grid-cols-4 gap-2">
          {stats.map(s => (
            <div key={s.value} className="flex flex-col items-center bg-brand-50 rounded-2xl py-3 px-1 border border-brand-100">
              <p className="text-lg font-extrabold text-brand-700 leading-none">{s.value}</p>
              <p className="text-[10px] text-brand-500 font-semibold text-center leading-tight mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Benefits checklist ─────────────────────────────────────────────── */}
      <section className="bg-white px-6 py-6 border-t border-slate-100">
        <h2 className="text-lg font-extrabold text-brand-700 mb-4 leading-tight">
          Everything you need to protect your relationship with your child
        </h2>
        <ul className="flex flex-col gap-3">
          {benefits.map((b) => (
            <li key={b} className="flex items-center gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-700 flex items-center justify-center text-white">
                <CheckIcon />
              </div>
              <span className="text-sm text-slate-700 font-medium">{b}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* ── Features grid ──────────────────────────────────────────────────── */}
      <section className="px-5 py-8" style={{ background: 'linear-gradient(180deg, #f0f5fc 0%, #e8f0fb 100%)' }}>
        <div className="flex items-center gap-2 mb-6">
          <div className="h-px flex-1 bg-brand-200" />
          <h2 className="text-sm font-extrabold text-brand-700 uppercase tracking-widest whitespace-nowrap px-3">
            Five Tools. One Platform.
          </h2>
          <div className="h-px flex-1 bg-brand-200" />
        </div>
        <div className="flex flex-col gap-3">
          {features.map((f) => (
            <div key={f.title}
              className={`flex gap-4 items-start rounded-2xl border p-4 ${f.accent} shadow-sm`}>
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-xl flex-shrink-0 ${f.iconBg} shadow`}>
                <span>{f.emoji}</span>
              </div>
              <div>
                <h3 className="font-bold text-slate-800 text-sm">{f.title}</h3>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── How it works ───────────────────────────────────────────────────── */}
      <section className="bg-white px-6 py-8">
        <h2 className="text-lg font-extrabold text-brand-700 mb-6 text-center">How it works</h2>
        <div className="flex flex-col gap-4">
          {[
            { step: '1', title: 'Tell us your situation', desc: 'Answer a few questions about your custody or CPS case.' },
            { step: '2', title: 'Get instant AI guidance', desc: 'Receive plain-language explanations of your rights and options.' },
            { step: '3', title: 'Generate your documents', desc: 'Draft court-ready filings and letters in minutes.' },
          ].map(item => (
            <div key={item.step} className="flex items-start gap-4">
              <div className="w-9 h-9 rounded-full bg-brand-700 text-white font-extrabold text-base flex items-center justify-center flex-shrink-0 shadow">
                {item.step}
              </div>
              <div>
                <p className="font-bold text-slate-800 text-sm">{item.title}</p>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Legal disclaimer ───────────────────────────────────────────────── */}
      <section className="bg-amber-50 px-6 py-4 border-y border-amber-200">
        <p className="text-xs text-amber-800 leading-relaxed">
          ⚠️ <strong>Legal Education Notice:</strong> GoodGuardian provides legal education and document
          templates, not legal advice. All generated documents require attorney review before use.
          For immediate child safety emergencies, call 911.
        </p>
      </section>

      {/* ── Waitlist ───────────────────────────────────────────────────────── */}
      <section className="px-6 py-10" style={{ background: 'linear-gradient(160deg, #1F497D, #2E75B6)' }}>
        <div className="flex flex-col items-center text-center mb-6">
          <GGLogo size={44} variant="light" />
          <h2 className="text-xl font-extrabold text-white mt-3 mb-1">Join the waitlist</h2>
          <p className="text-sm text-blue-200">
            Be first to know when we launch. No spam, ever.
          </p>
        </div>

        {submitted ? (
          <div className="bg-white/15 backdrop-blur rounded-2xl border border-white/25 text-center py-6 px-4">
            <p className="text-3xl mb-2">✅</p>
            <p className="font-extrabold text-white text-base">You're on the list!</p>
            <p className="text-xs text-blue-200 mt-1 mb-4">We'll email you the moment we launch.</p>
            <button
              onClick={() => navigate('/register')}
              className="w-full py-3 rounded-xl bg-white text-brand-700 font-extrabold text-sm"
            >
              Try the app now →
            </button>
          </div>
        ) : (
          <form onSubmit={handleWaitlist} className="flex flex-col gap-3">
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="w-full px-4 py-3.5 rounded-xl bg-white/15 border border-white/30 text-white placeholder-blue-200 text-sm outline-none focus:border-white focus:bg-white/20"
            />
            {error && <p className="text-red-300 text-xs -mt-1">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-xl bg-white text-brand-700 font-extrabold text-sm shadow-lg disabled:opacity-60 active:scale-95 transition-transform"
            >
              {loading ? 'Joining…' : 'Join Waitlist →'}
            </button>
          </form>
        )}
      </section>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <footer style={{ background: '#13305e' }} className="text-blue-200/70 text-xs px-6 py-6">
        <div className="flex flex-col items-center gap-2 mb-4">
          <GGLogo size={32} variant="light" />
          <p className="font-extrabold text-white text-sm tracking-wide">GoodGuardian</p>
          <p className="text-[10px] tracking-[0.18em] uppercase text-blue-300/60">AI Family Law Platform</p>
        </div>
        <p className="text-center">© {new Date().getFullYear()} GoodGuardian. All rights reserved.</p>
        <p className="mt-2 text-center">
          <Link to="/privacy" className="underline hover:text-white">Privacy Policy</Link>
          {' · '}
          <Link to="/terms" className="underline hover:text-white">Terms of Service</Link>
        </p>
      </footer>

    </div>
  )
}
